import { TeamPhotoStack } from "@/grid/components/TeamPhotoStack";
import { getCell } from "@/grid/data/assemble-grid";
import { MetricCell } from "@/grid/components/MetricCell";
import { calculateTeamGrade, getOrdinal } from "@/grid/lib/scorecard";
import { DEFAULT_THRESHOLDS } from "@/grid/lib/pacing";
import type { DepartmentDefinition, GridTeam } from "@/grid/core/types";
import { cn } from "@/lib/cn";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";

interface MobileTeamCarouselProps {
  teams: GridTeam[];
  department: DepartmentDefinition;
  isLoading: boolean;
  showLabels: boolean;
  showValues: boolean;
  rankings?: Map<number, number>;
  rankingCount?: number;
}

const SKELETON_COUNT = 6;
const CELL_SIZE = "h-16 w-16";
const CELL_SIZE_PX = 64;

export function MobileTeamCarousel({
  teams,
  department,
  isLoading,
  showLabels,
  showValues,
  rankings,
  rankingCount = 3,
}: MobileTeamCarouselProps) {
  const thresholds = department.thresholds ?? DEFAULT_THRESHOLDS;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    skipSnaps: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = useCallback(
    (index: number) => {
      emblaApi?.scrollTo(index);
    },
    [emblaApi],
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (emblaApi && teams.length > 0) {
      emblaApi.reInit();
    }
  }, [emblaApi, teams]);

  const itemCount = isLoading ? SKELETON_COUNT : teams.length;
  const currentTeam = teams[selectedIndex];
  const currentRank = currentTeam && rankings ? rankings.get(currentTeam.id) : undefined;
  const showRankHighlight =
    department.showRanking && currentRank !== undefined && currentRank <= rankingCount;

  const renderGrid = (team: GridTeam) => (
    <div className="inline-flex flex-col gap-1.5">
      {showLabels ? (
        <div className="flex items-center gap-1.5">
          <div style={{ width: CELL_SIZE_PX }} className="shrink-0" />
          {department.periods.map((period) => (
            <div
              key={period.id}
              style={{ width: CELL_SIZE_PX }}
              className="flex items-center justify-center text-xs font-medium text-[var(--color-muted-foreground)]"
            >
              {period.shortLabel ?? period.label}
            </div>
          ))}
        </div>
      ) : null}

      {department.metrics.map((metric) => (
        <div key={metric.id} className="flex items-center gap-1.5">
          {showLabels ? (
            <div
              style={{ width: CELL_SIZE_PX }}
              className="shrink-0 pr-1.5 text-right text-[11px] font-medium text-[var(--color-muted-foreground)]"
            >
              {metric.label}
            </div>
          ) : null}

          {department.periods.map((period) => {
            const cell = getCell(team, metric.id, period.id);
            return (
              <MetricCell
                key={`${metric.id}-${period.id}`}
                actual={cell?.actual ?? null}
                target={cell?.target ?? null}
                pacingTarget={cell?.pacingTarget ?? null}
                targetSource={cell?.targetSource ?? null}
                metric={metric}
                period={period}
                teamName={team.name}
                showValues={showValues}
                thresholds={thresholds}
                className={cn(CELL_SIZE, "rounded-xl")}
              />
            );
          })}
        </div>
      ))}
    </div>
  );

  const renderSkeletonGrid = () => (
    <div className="inline-flex flex-col gap-1.5">
      {showLabels ? (
        <div className="flex items-center gap-1.5">
          <div style={{ width: CELL_SIZE_PX }} className="shrink-0" />
          {department.periods.map((period) => (
            <div key={period.id} style={{ width: CELL_SIZE_PX }} className="flex justify-center">
              <div className="h-3 w-8 animate-pulse rounded bg-[var(--color-muted)]" />
            </div>
          ))}
        </div>
      ) : null}

      {department.metrics.map((metric, rowIndex) => (
        <div key={metric.id} className="flex items-center gap-1.5">
          {showLabels ? (
            <div style={{ width: CELL_SIZE_PX }} className="flex shrink-0 justify-end pr-1.5">
              <div className="h-3 w-12 animate-pulse rounded bg-[var(--color-muted)]" />
            </div>
          ) : null}
          {department.periods.map((period) => (
            <div
              key={`${metric.id}-${period.id}`}
              className={cn(CELL_SIZE, "animate-pulse rounded-xl bg-[var(--color-muted)]/60")}
              style={{ animationDelay: `${rowIndex * 50}ms` }}
            />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex w-full flex-col items-center">
      <div
        className={cn(
          "relative mb-2 flex flex-col items-center",
          showRankHighlight && "rounded-2xl px-5 py-2 ring-1",
          showRankHighlight &&
            currentRank === 1 &&
            "bg-amber-50 ring-amber-300/40 dark:bg-amber-950/20 dark:ring-amber-400/30",
          showRankHighlight &&
            currentRank === 2 &&
            "bg-slate-50 ring-slate-300/40 dark:bg-slate-800/20 dark:ring-slate-400/30",
          showRankHighlight &&
            currentRank === 3 &&
            "bg-orange-50 ring-orange-300/40 dark:bg-orange-950/20 dark:ring-orange-400/30",
          showRankHighlight &&
            currentRank &&
            currentRank > 3 &&
            "bg-[var(--color-muted)]/30 ring-[var(--color-muted-foreground)]/20",
        )}
      >
        {showRankHighlight && currentRank ? (
          <div className="pointer-events-none absolute inset-x-0 -top-2 text-center">
            <span
              className={cn(
                "inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold",
                currentRank === 1 && "bg-amber-400 text-amber-950",
                currentRank === 2 && "bg-slate-400 text-slate-900",
                currentRank === 3 && "bg-orange-400 text-orange-950",
                currentRank > 3 &&
                  "bg-[var(--color-muted-foreground)] text-[var(--color-background)]",
              )}
            >
              {getOrdinal(currentRank)}
            </span>
          </div>
        ) : null}

        <div className="flex flex-col items-center gap-1.5">
          <div className="flex items-baseline gap-2.5">
            {isLoading ? (
              <div className="h-6 w-28 animate-pulse rounded bg-[var(--color-muted)]" />
            ) : currentTeam ? (
              <>
                <h2 className="text-lg font-bold tracking-tight text-[var(--color-foreground)]">
                  {currentTeam.name}
                </h2>
                <span className="text-2xl font-black tracking-tight text-[var(--color-foreground)]">
                  {calculateTeamGrade(currentTeam, department)}
                </span>
              </>
            ) : null}
          </div>
          {currentTeam ? (
            <TeamPhotoStack teamName={currentTeam.name} department={department.id} />
          ) : null}
        </div>
      </div>

      <div className="w-full overflow-hidden" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {isLoading
            ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="flex w-full flex-none justify-center px-4"
                >
                  {renderSkeletonGrid()}
                </div>
              ))
            : teams.map((team) => (
                <div key={team.id} className="flex w-full flex-none justify-center px-4">
                  {renderGrid(team)}
                </div>
              ))}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-center gap-2">
        {Array.from({ length: itemCount }).map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => scrollTo(index)}
            className={cn(
              "h-2 rounded-full transition-all duration-300",
              index === selectedIndex
                ? "w-6 bg-[var(--color-foreground)]"
                : "w-2 bg-[var(--color-muted-foreground)]/25 active:bg-[var(--color-muted-foreground)]/50",
            )}
            aria-label={`Go to team ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
